import React from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import normalizeUrl from 'normalize-url'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useAppSelector } from 'reduxTyped'
import { v4 } from 'uuid'
import { z } from 'zod'
import { l, Loader, SectionMessage, TextInputV2 } from '@manychat/manyui'

import { AiAutomationKnowledgeSelectors } from 'apps/aiAutomation/store/knowledge'
import {
  KnowledgeEntryState,
  KnowledgeEntryType,
  LinkEntry,
  LinkEntryDraft,
} from 'shared/api/requests/aiBusinessKnowledge/schemas'

import { KnowledgeValidation } from '../../lib/validation'
import { EntryControls } from '../EntryControls/EntryControls'

import { getErrorContent } from './lib/getErrorContent'

import cm from './LinkEntryEditor.module.css'

interface LinkEntryEditorProps {
  entry: LinkEntry | null
  onSubmit: (entry: LinkEntryDraft | LinkEntry) => void
  isLoading: boolean
}

export const LinkEntryEditor = ({ entry, onSubmit, isLoading }: LinkEntryEditorProps) => {
  const links = useAppSelector(AiAutomationKnowledgeSelectors.getUsedLinks)

  const { handleSubmit, register, formState } = useForm({
    defaultValues: {
      url: entry?.data?.url || '',
      title: entry?.title || '',
    },
    resolver: zodResolver(
      z.object({
        title: KnowledgeValidation.getTitleSchema(),
        url: z
          .string()
          .min(1, l.translate('Add a link to continue'))
          .url(l.translate('Invalid link'))
          .refine(
            (value) => {
              return (
                entry ||
                links.filter((link) => normalizeUrl(link) === normalizeUrl(value)).length === 0
              )
            },
            {
              message: l.translate('This link is already added.'),
            },
          ),
      }),
    ),
  })

  const submit: SubmitHandler<{ url: string; title: string }> = ({ url, title }) => {
    onSubmit({
      id: entry?.id ?? v4(),
      type: KnowledgeEntryType.LINK,
      title,
      data: {
        url: entry?.data?.url ?? url,
      },
    })
  }

  const error = entry?.error_code ? getErrorContent(entry.error_code) : null

  const { ref: urlRef, ...urlProps } = register('url')
  const { ref: titleRef, ...titleProps } = register('title')

  return (
    <>
      <div className="h-full">
        {error && (
          <SectionMessage
            title={error.title}
            description={error.description}
            variant="error"
            className="m-b"
          />
        )}

        <TextInputV2
          inputRef={urlRef}
          {...urlProps}
          label={entry ? l.translate('<l10n context="Noun"/>Link') : l.translate('Add a link')}
          errorText={formState.errors.url?.message}
          helpText={l.translate('AI scans the content of this page, not the entire site.')}
          invalid={Boolean(formState.errors.url?.message)}
          placeholder={l.translate('e.g. https://manychat.com')}
          disabled={Boolean(entry) || isLoading}
        />

        <TextInputV2
          inputRef={titleRef}
          {...titleProps}
          errorText={formState.errors.title?.message}
          invalid={Boolean(formState.errors.title?.message)}
          disabled={isLoading || Boolean(error)}
          className="m-t-lg"
          label={l.translate('What’s this about?')}
          placeholder={l.translate('e.g. Price')}
        />

        {entry?.state === KnowledgeEntryState.IN_PROCESS && (
          <div className={cm.loader}>
            <Loader size={32} />

            <p className="font-semibold">{l.translate('AI is scanning the link...')}</p>

            <p className="m-0">{l.translate('This’ll take a few seconds')}</p>
          </div>
        )}
      </div>

      <EntryControls entry={entry} onSave={handleSubmit(submit)} isLoading={isLoading} />
    </>
  )
}
